import React, { useState, useMemo } from 'react';
import useGetProducts from '../../../hooks/products/useGetProducts';
import { FiEdit, FiTrash2, FiSearch } from 'react-icons/fi';
import { Dialog } from '@headlessui/react';
import { Link } from 'react-router-dom';
/* import { EDIT_PRODUCT } from '../../../lib/routes'; */


const ViewProducts = () => {
  const { products, loading, error } = useGetProducts();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const openDeleteModal = (product) => {
    setSelectedProduct(product);
    setIsDeleteOpen(true);
  };

  const closeDeleteModal = () => {
    setSelectedProduct(null);
    setIsDeleteOpen(false);
  };

  const handleDelete = () => {
    console.log('Deleting:', selectedProduct.id);
    closeDeleteModal();
  };

  const filteredProducts = useMemo(() => {
    let filtered = products;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name?.toLowerCase().includes(term) ||
          p.sku?.toLowerCase().includes(term)
      );
    }

    if (selectedCategory !== 'All') {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }

    return filtered;
  }, [products, searchTerm, selectedCategory]);

  const categoryOptions = useMemo(() => {
    const categories = new Set(products.map(p => p.category).filter(Boolean));
    return ['All', ...Array.from(categories)];
  }, [products]);

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <header className="sticky top-0 z-10 bg-gray-50 pb-6">
          <h1 className="text-4xl font-extrabold text-gray-800">🛍 Admin Products</h1>
          <p className="text-gray-500 mt-2">Manage your inventory here</p>
        </header>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mt-8 items-center">
          <div className="relative w-full md:w-1/2">
            <input
              type="text"
              placeholder="Search by name or SKU"
              className="w-full border rounded-md py-2 px-10 focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FiSearch className="absolute top-2.5 left-3 text-gray-400" />
          </div>

          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="border rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            {categoryOptions.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        {/* Product Table */}
        {loading ? (
          <div className="mt-12 animate-pulse text-center text-gray-400">Loading products...</div>
        ) : error ? (
          <div className="text-center text-red-600 mt-20 text-lg">⚠️ {error.message}</div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center mt-20 text-gray-500">No products found.</div>
        ) : (
          <div className="overflow-x-auto mt-10">
            <table className="min-w-full bg-white rounded-lg shadow-sm overflow-hidden text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="text-left px-4 py-3">Image</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Description</th>
                  <th className="text-left px-4 py-3">Price</th>
                  <th className="text-left px-4 py-3">SKU</th>
                  <th className="text-left px-4 py-3">Category</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => (
                  <tr key={product.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <img
                        src={product.imageUrl || '/placeholder.png'}
                        alt={product.name}
                        className="h-12 w-12 object-cover rounded-md"
                      />
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{product.name}</td>
                    <td className="px-4 py-3 text-gray-600">{product.description || '-'}</td>
                    <td className="px-4 py-3 text-purple-600 font-semibold">
                      ${product.price.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{product.sku}</td>
                    <td className="px-4 py-3 text-gray-500">{product.category || '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/protected/edit/${product.id}`}
                        className="text-blue-600 hover:text-blue-800 mr-4 inline-flex items-center"
                      >
                        <FiEdit className="mr-1" />
                        Edit
                      </Link>
                      <button
                        onClick={() => openDeleteModal(product)}
                        className="text-red-600 hover:text-red-800 inline-flex items-center"
                      >
                        <FiTrash2 className="mr-1" />
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={isDeleteOpen}
        onClose={closeDeleteModal}
        className="fixed z-50 inset-0 p-4 flex items-center justify-center bg-black/30"
      >
        <Dialog.Panel className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
          <Dialog.Title className="text-lg font-semibold text-gray-800 mb-2">
            Confirm Deletion
          </Dialog.Title>
          <Dialog.Description className="text-gray-600 mb-4">
            Are you sure you want to delete{' '}
            <span className="font-bold">{selectedProduct?.name}</span>?
          </Dialog.Description>
          <div className="flex justify-end gap-3">
            <button
              onClick={closeDeleteModal}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-md"
            >
              Delete
            </button>
          </div>
        </Dialog.Panel>
      </Dialog>
    </div>
  );
};

export default ViewProducts;
